import React, { useState, useEffect } from 'react';
import { confirmSignIn } from 'aws-amplify/auth';
import { TAGLINE, noOrphan } from '../brand';

const TEAL = '#4A90E2';

function t(isDark) {
  if (isDark) return {
    bg: '#0A1B3B', card: '#0A1B3B', border: 'rgba(120,183,255,0.28)',
    text: '#FFFFFF', textSec: '#5E6F8E', textMuted: '#5E6F8E',
    inputBg: '#0A1B3B', inputText: '#fff', inputPh: 'gray-600',
    logoFilter: 'brightness(0) invert(1)',
    errBg: '#FDE7E1', errBorder: '#C2410C', errText: '#F0563C',
    labelText: '#5E6F8E',
  };
  return {
    bg: '#FFFFFF', card: '#F6F4EE', border: 'rgba(10,27,59,0.12)',
    text: '#0A1B3B', textSec: '#3A4D6E', textMuted: '#5E6F8E',
    inputBg: '#F6F4EE', inputText: '#0A1B3B', inputPh: 'gray-400',
    logoFilter: 'none',
    errBg: '#FEF2F2', errBorder: '#FECACA', errText: '#C2410C',
    labelText: '#3A4D6E',
  };
}

function SunIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;
}
function MoonIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
}

export default function LoginPage({ onLogin, error, setError }) {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('byrdsense-home-theme') === 'dark'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('byrdsense-home-theme', isDark ? 'dark' : 'light'); } catch {}
  }, [isDark]);
  const toggle = () => setIsDark(d => !d);

  const c = t(isDark);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [newPasswordMode, setNewPasswordMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const inputStyle = {
    backgroundColor: c.inputBg,
    border: `1px solid ${c.border}`,
    color: c.inputText,
    transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await onLogin(email, password);
      if (result?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNewPasswordMode(true);
        setError('');
      }
    } catch {
      // error is set by parent
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: c.bg, transition: 'background-color 0.2s' }}>
      {/* Theme toggle — top right */}
      <button onClick={toggle} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        className="fixed top-4 right-4 z-50 flex items-center justify-center w-10 h-10 rounded-lg transition-colors"
        style={{ color: c.textMuted }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
        {isDark ? <SunIcon /> : <MoonIcon />}
      </button>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6 fade-in">
          <img
            src={isDark ? '/assets/brand/byrdsense-wordmark-reversed.svg' : '/assets/brand/byrdsense-wordmark-color.svg'}
            alt="Byrdsense"
            className="mx-auto mb-3"
            style={{ width: '100%', maxWidth: 300, height: 'auto' }}
          />
          <p className="text-sm font-medium tracking-wide" style={{ color: c.textMuted, transition: 'color 0.2s' }}>{noOrphan(TAGLINE)}</p>
        </div>

        {/* Login Card */}
        <div className="rounded-2xl p-8 fade-in" style={{ backgroundColor: c.card, border: `1px solid ${c.border}`, transition: 'background-color 0.2s, border-color 0.2s' }}>
          {!newPasswordMode ? (
            <form onSubmit={handleSubmit}>
              <h2 className="text-xl font-bold mb-6" style={{ color: c.text, transition: 'color 0.2s' }}>Sign in to your account</h2>

              {error && error !== 'FORCE_NEW_PASSWORD' && (
                <div className="rounded-lg px-4 py-3 mb-4 text-sm" style={{ backgroundColor: c.errBg, border: `1px solid ${c.errBorder}`, color: c.errText }}>
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-1.5" style={{ color: c.labelText, transition: 'color 0.2s' }}>Email</label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  required autoComplete="email"
                  className={`w-full px-4 py-3 rounded-xl text-base outline-none transition placeholder-${c.inputPh}`}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = TEAL}
                  onBlur={e => e.target.style.borderColor = c.border}
                  placeholder="you@company.com"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-1.5" style={{ color: c.labelText, transition: 'color 0.2s' }}>Password</label>
                <input
                  type="password" value={password} onChange={e => setPassword(e.target.value)}
                  required autoComplete="current-password"
                  className={`w-full px-4 py-3 rounded-xl text-base outline-none transition placeholder-${c.inputPh}`}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = TEAL}
                  onBlur={e => e.target.style.borderColor = c.border}
                  placeholder="Enter your password"
                />
              </div>

              <button
                type="submit" disabled={loading}
                className="w-full py-3.5 rounded-xl font-bold text-white text-base
                  disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ backgroundColor: TEAL }}
                onMouseEnter={e => { if (!loading) e.target.style.opacity = '0.85'; }}
                onMouseLeave={e => e.target.style.opacity = '1'}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleNewPassword}>
              <h2 className="text-xl font-bold mb-2" style={{ color: c.text, transition: 'color 0.2s' }}>Set a new password</h2>
              <p className="text-sm mb-6" style={{ color: c.textMuted, transition: 'color 0.2s' }}>Your account requires a new password.</p>

              {error && (
                <div className="rounded-lg px-4 py-3 mb-4 text-sm" style={{ backgroundColor: c.errBg, border: `1px solid ${c.errBorder}`, color: c.errText }}>
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-1.5" style={{ color: c.labelText, transition: 'color 0.2s' }}>New Password</label>
                <input
                  type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  required autoComplete="new-password"
                  className={`w-full px-4 py-3 rounded-xl text-base outline-none transition placeholder-${c.inputPh}`}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = TEAL}
                  onBlur={e => e.target.style.borderColor = c.border}
                  placeholder="Minimum 8 characters"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-1.5" style={{ color: c.labelText, transition: 'color 0.2s' }}>Confirm Password</label>
                <input
                  type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  required autoComplete="new-password"
                  className={`w-full px-4 py-3 rounded-xl text-base outline-none transition placeholder-${c.inputPh}`}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = TEAL}
                  onBlur={e => e.target.style.borderColor = c.border}
                  placeholder="Re-enter your password"
                />
              </div>

              <button
                type="submit" disabled={loading}
                className="w-full py-3.5 rounded-xl font-bold text-white text-base
                  disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ backgroundColor: TEAL }}
                onMouseEnter={e => { if (!loading) e.target.style.opacity = '0.85'; }}
                onMouseLeave={e => e.target.style.opacity = '1'}
              >
                {loading ? 'Setting password...' : 'Set Password & Continue'}
              </button>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}
